// Editorial: big serif headlines on calm paper, small bold labels, a rule and
// one line at the foot. From the Form hair studio concepts.
import { flow, textBlock, brand, footer, photoBand, pairBlock, pairStory, pills, rule, display, label, body, strong, widthOf, nameTag , codeTicket, finePrint } from '../kit'

const base = (env) => ({ fr: env.frame, c: env.post.content, k: env.theme.c, w: widthOf(env.frame) })
const times = (c) => (c.times || []).filter(Boolean).slice(0, 3)
const italic = (t) => (t && !/\*/.test(t) ? `*${t}*` : t)

export default {
  freeSlots(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.eyebrow, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 96, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, k.ink, 196 * s), x: fr.left - 6, w: w - 190, maxLines: 3 }), { gap: 44, slot: 'headline' }),
      textBlock(env.day, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 56, flex: 1, slot: 'date' }),
      pills(env, fr, times(c), { bg: k.ink, fg: k.paper }),
      c.more ? textBlock('More times available', () => ({ ...label(env, k.mutedOnPaper), x: fr.left, w }), { gap: 22, slot: 'more' }) : null,
      footer(env, fr, k.ink),
    ])
  },

  oneSlot(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.headline, s => ({ ...display(env, k.ink, 116 * s), x: fr.left - 4, w: w + 4, maxLines: 3 }), { gap: 96, slot: 'headline' }),
      textBlock(env.day, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 56, flex: 1, slot: 'date' }),
      textBlock(c.time, s => ({ ...display(env, k.ink, 250 * s), x: fr.left - 6, w: w + 6, maxLines: 1 }), { gap: 6, slot: 'time' }),
      textBlock(italic(c.service), s => ({ ...display(env, k.ink, 66 * s), x: fr.left, w, maxLines: 2 }), { gap: 16, slot: 'service' }),
      textBlock(c.line, () => ({ ...body(env, k.mutedOnPaper, 40), x: fr.left, w, maxLines: 2 }), { gap: 36, slot: 'line' }),
      footer(env, fr, k.ink),
    ])
  },

  freshWork(p, env) {
    const { fr, c, k } = base(env)
    photoBand(p, env, {
      key: 'main', photoH: 820, band: k.ink,
      blocks: (f) => [
        f.story ? brand(env, f, k.pop) : null,
        textBlock(c.detail, () => ({ ...label(env, k.pop), x: f.left, w: widthOf(f) }), { gap: f.story ? 26 : 0, slot: 'detail' }),
        textBlock(c.headline, s => ({ ...display(env, k.paper, 112 * s), x: f.left - 4, w: widthOf(f) + 4, maxLines: 3 }), { gap: 20, slot: 'headline' }),
        footer(env, f, k.paper, k.accent),
      ],
    })
    if (!fr.story) nameTag(p, env, 54, 54, k.ink, k.paper)
  },

  beforeAfter(p, env) {
    const { fr, c, k } = base(env)
    const words = (f) => [
      textBlock(c.headline, s => ({ ...display(env, k.ink, 100 * s), x: f.left - 4, w: widthOf(f) + 4, maxLines: 2 }), { slot: 'headline' }),
      textBlock(c.detail, () => ({ ...label(env, k.ink), x: f.left, w: widthOf(f) }), { gap: 16, slot: 'detail' }),
      footer(env, f, k.ink),
    ]
    if (fr.story) {
      pairStory(p, env, { bg: k.paper, tagBg: k.ink, tagFg: k.paper, blocks: words })
      return
    }
    p.rect(0, 0, fr.W, fr.H, k.paper)
    const [head, detail, foot] = words(fr)
    flow(p, fr, [brand(env, fr, k.ink), pairBlock(env, fr, { h: 660, tagBg: k.ink, tagFg: k.paper }), head && { ...head, gap: 44 }, detail, foot])
  },

  review(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    const stars = Math.max(1, Math.min(5, Number(c.rating) || 5))
    flow(p, fr, [
      brand(env, fr, k.ink),
      {
        gap: 90,
        draw: (pp, y) => {
          pp.text('“', { ...display(env, k.accent, 300), upper: false, x: fr.right - 190, y: y - 70, w: 200, maxLines: 1, lh: 1 })
          pp.stars(fr.left, y, 46, stars, k.ink, 12)
          return y + 46
        },
      },
      textBlock(c.quote ? `“${c.quote}”` : '', s => ({ ...display(env, k.ink, 104 * s), x: fr.left, w, maxLines: 8, maxH: 660 * s, minSize: 40 }), { gap: 44, slot: 'quote' }),
      textBlock(c.name, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 44, flex: 1, slot: 'name' }),
      textBlock(c.service, () => ({ ...body(env, k.mutedOnPaper, 36), x: fr.left, w, maxLines: 1 }), { gap: 8, slot: 'service' }),
      footer(env, fr, k.ink),
    ])
  },

  team(p, env) {
    const { fr, c, k } = base(env)
    photoBand(p, env, {
      key: 'main', photoH: 780, band: k.ink,
      blocks: (f) => [
        f.story ? brand(env, f, k.pop) : null,
        textBlock(c.eyebrow, () => ({ ...label(env, k.pop), x: f.left, w: widthOf(f) }), { gap: f.story ? 26 : 0, slot: 'eyebrow' }),
        textBlock(c.name, s => ({ ...display(env, k.paper, 124 * s), x: f.left - 4, w: widthOf(f) + 4, maxLines: 1 }), { gap: 16, slot: 'name' }),
        textBlock(c.role, () => ({ ...label(env, k.pop), x: f.left, w: widthOf(f) }), { gap: 14, slot: 'role' }),
        textBlock(c.line, () => ({ ...body(env, k.mutedOnInk, 38), x: f.left, w: widthOf(f), maxLines: 2 }), { gap: 16, slot: 'line' }),
        footer(env, f, k.paper, k.accent),
      ],
    })
    if (!fr.story) nameTag(p, env, 54, 54, k.ink, k.paper)
  },

  priceList(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    const page = (env.slide && env.slide.page) || 0
    const items = (c.items || []).filter(it => it && it.name).slice(page * 4, page * 4 + 4)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.title, s => ({ ...display(env, k.ink, 150 * s), x: fr.left - 6, w: w + 6, maxLines: 2 }), { gap: 90, slot: 'title' }),
      {
        gap: 64, flex: 1,
        draw: (pp, y, s) => {
          let yy = y
          items.forEach((it) => {
            pp.line(fr.left, yy + 1, fr.right, yy + 1, k.ink, 2)
            const n = pp.text(it.name, { ...display(env, k.ink, Math.max(44, 54 * s)), x: fr.left, y: yy + 26, w: w - 250, maxLines: 1, slot: 'items' })
            const d = it.detail ? pp.text(it.detail, { ...body(env, k.mutedOnPaper, 32), x: fr.left, y: n.bottom + 8, w: w - 250, maxLines: 1 }) : n
            const pr = pp.text(it.price || '', { ...strong(env, k.ink, 44), x: fr.right - 240, y: yy + 30, w: 240, align: 'right', maxLines: 1, slot: 'items' })
            yy = Math.max(d.bottom, pr.bottom) + 26
          })
          return yy
        },
      },
      textBlock(c.note, () => ({ ...body(env, k.mutedOnPaper, 34), x: fr.left, w, maxLines: 2 }), { gap: 20, slot: 'note' }),
      footer(env, fr, k.ink),
    ])
  },

  tips(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.ink)
    flow(p, fr, [
      brand(env, fr, k.paper),
      {
        gap: 100,
        draw: (pp, y) => {
          pp.text(c.number || '', { ...display(env, k.accent, 210), x: fr.right - 420, y: y - 34, w: 420, align: 'right', maxLines: 1, minSize: 120, slot: 'number' })
          const l = pp.text(c.label || '', { ...label(env, k.accent), x: fr.left, y, w: w - 440, slot: 'label' })
          return Math.max(l.bottom, y + 150)
        },
      },
      textBlock(c.headline, s => ({ ...display(env, k.paper, 114 * s), x: fr.left - 4, w: w + 4, maxLines: 3 }), { gap: 56, slot: 'headline' }),
      rule(fr, k.accent, { gap: 52 }),
      textBlock(c.body, () => ({ ...body(env, k.paper, 44), x: fr.left, w: w - 30, maxLines: 5 }), { gap: 40, slot: 'body' }),
      footer(env, fr, k.paper, k.accent),
    ])
  },

  question(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      {
        gap: 90,
        draw: (pp, y) => {
          pp.text('?', { ...display(env, k.accent, 260), x: fr.right - 200, y: y - 50, w: 200, align: 'right', maxLines: 1 })
          const l = pp.text('You asked', { ...label(env, k.ink), x: fr.left, y, w: w - 240 })
          return Math.max(l.bottom, y + 120)
        },
      },
      textBlock(c.headline, s => ({ ...display(env, k.ink, 124 * s), x: fr.left - 4, w: w + 4, maxLines: 4 }), { gap: 40, slot: 'headline' }),
      rule(fr, k.ink, { gap: 52 }),
      textBlock(c.body, () => ({ ...body(env, k.ink, 44), x: fr.left, w: w - 30, maxLines: 5 }), { gap: 40, slot: 'body' }),
      footer(env, fr, k.ink),
    ])
  },

  // An announcement: a season, a date, some news. Big words and nothing else
  announce(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.eyebrow, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 96, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, k.ink, 150 * s), x: fr.left - 6, w: w + 6, maxLines: 4 }), { gap: 36, slot: 'headline' }),
      rule(fr, k.accent, { gap: 64, width: 4 }),
      textBlock(c.body, () => ({ ...body(env, k.ink, 44), x: fr.left, w: w - 30, maxLines: 4 }), { gap: 44, flex: 1, slot: 'body' }),
      footer(env, fr, k.ink),
    ])
  },

  offer(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.eyebrow, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 96, slot: 'eyebrow' }),
      textBlock(c.value, s => ({ ...display(env, k.ink, 230 * s), x: fr.left - 6, w: w + 6, maxLines: 2 }), { gap: 30, slot: 'value' }),
      textBlock(italic(c.service), s => ({ ...display(env, k.ink, 68 * s), x: fr.left, w, maxLines: 2 }), { gap: 18, slot: 'service' }),
      textBlock(c.price, () => ({ ...strong(env, k.ink, 40), x: fr.left, w, maxLines: 2 }), { gap: 56, flex: 1, slot: 'price' }),
      textBlock(c.dates, () => ({ ...body(env, k.mutedOnPaper, 36), x: fr.left, w, maxLines: 1 }), { gap: 10, slot: 'dates' }),
      codeTicket(env, fr, k.ink),
      finePrint(env, fr, k.mutedOnPaper),
      footer(env, fr, k.ink),
    ])
  },

  giftVouchers(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.eyebrow, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 96, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, k.ink, 118 * s), x: fr.left - 4, w: w + 4, maxLines: 3 }), { gap: 32, slot: 'headline' }),
      {
        gap: 60, flex: 1,
        draw: (pp, y, s) => {
          const cw = Math.min(w, 760)
          const ch = Math.round(430 * s)
          pp.rect(fr.left, y, cw, ch, k.ink)
          pp.text(env.theme.name, { ...label(env, k.paper), x: fr.left + 44, y: y + 40, w: cw - 330 })
          pp.text('Gift voucher', { ...label(env, k.accent), x: fr.left + cw - 330, y: y + 40, w: 286, align: 'right' })
          pp.text(c.from || '', { ...display(env, k.accent, 104 * s), x: fr.left + 44, y: y + ch - 44 - 104 * s * 0.95, w: cw - 88, maxLines: 1, slot: 'from' })
          return y + ch
        },
      },
      footer(env, fr, k.ink),
    ])
  },
}
